/* eslint-disable react/jsx-props-no-spreading */
import Konva from 'konva';
import { KonvaEventObject } from 'konva/lib/Node';
import React, { FC, memo, ReactNode } from 'react';
import { Ellipse, Group, Line, Rect, Text } from 'react-konva';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getGridCellSize } from '../model/selectors/getGridCellSize';
import { Shape, ShapeType, WarehouseMapTool } from '../model/types';
import { BoxShapeLabel } from './BoxShapeLabel';

interface WarehouseMapShapesProps {
  activeTool: WarehouseMapTool;
  shapes: Shape[];
  onDragMove: (e: KonvaEventObject<MouseEvent>) => void;
  onDragEnd: (e: KonvaEventObject<MouseEvent>) => void;
  onTransformEnd: (e: KonvaEventObject<MouseEvent>) => void;
}

export const WarehouseMapShapes: FC<WarehouseMapShapesProps> = memo((props) => {
  const { shapes, activeTool, onDragMove, onDragEnd, onTransformEnd } = props;

  const gridCellSize = useAppSelector(getGridCellSize);

  const commonOptions = {
    draggable: activeTool === WarehouseMapTool.POINTER,
    onDragMove,
    onDragEnd,
    onTransformEnd,
  };

  const boxLabels: ReactNode[] = [];

  const syncBoxLabel = (node: Konva.Node): void => {
    const label = node.getStage()?.findOne(`#box-label-${node.id()}`);

    if (!label) {
      return;
    }

    label.position({ x: node.x(), y: node.y() });
    label.scale({ x: node.scaleX(), y: node.scaleY() });
    label.rotation(node.rotation());
    label.getLayer()?.batchDraw();
  };

  const getShapes = (): Nullable<ReactNode> => {
    return shapes.map((shape) => {
      const deleteShape = (e: KonvaEventObject<MouseEvent>): void => {
        e.evt.preventDefault();
        shape.onDelete?.();
      };

      const fontSize = 'fontSize' in shape && shape.fontSize ? shape.fontSize : 12;

      const boxText =
        'label' in shape && 'width' in shape
          ? `${shape.label}\n${roundNumber((shape.width / gridCellSize) * (shape.height / gridCellSize))} m²`
          : '';

      const getPolygonSquare = (points: number[]): number => {
        const pointsCount = points.length / 2;
        let square = 0;

        for (let i = 0; i < pointsCount; i++) {
          const x1 = points[2 * i];
          const y1 = points[2 * i + 1];
          const x2 = points[2 * ((i + 1) % pointsCount)];
          const y2 = points[2 * ((i + 1) % pointsCount) + 1];

          square += (x1 / gridCellSize) * (y2 / gridCellSize) - (x2 / gridCellSize) * (y1 / gridCellSize);
        }

        return Math.abs(square) / 2;
      };

      const polygonBoxText =
        'label' in shape && 'points' in shape ? `${shape.label}\n${roundNumber(getPolygonSquare(shape.points))} m²` : '';

      const findPolygonCenter = (coords: number[]): [number, number] => {
        let sumX = 0;
        let sumY = 0;
        const pointsCount = coords.length / 2;

        for (let i = 0; i < coords.length; i += 2) {
          sumX += coords[i];
          sumY += coords[i + 1];
        }

        const centerX = sumX / pointsCount;
        const centerY = sumY / pointsCount;

        return [centerX, centerY];
      };

      switch (shape.type) {
        case ShapeType.RECTANGLE: {
          // Scale only fits the rectangle to the plan. The label is drawn in
          // canvas pixels, centered, and sized so both lines stay inside the box.
          const scaleX = shape.scaleX || 1;
          const scaleY = shape.scaleY || 1;
          const visualWidth = Math.max(shape.width * scaleX, 1);
          const visualHeight = Math.max(shape.height * scaleY, 1);
          const [labelLine = '', areaLine = ''] = boxText.split('\n');
          const longestLine = Math.max(labelLine.length, areaLine.length, 1);
          const fittedFont = Math.min(12, (visualHeight * 0.86) / 2.12, (visualWidth * 0.9) / (longestLine * 0.55));
          const areaFont = Math.max(1, Math.round(fittedFont * 10) / 10);
          const labelFontLimit = Math.min(12, visualHeight * 0.46, (visualWidth * 0.88) / (Math.max(labelLine.length, 1) * 0.58));
          const requestedLabelFont = shape.type === ShapeType.RECTANGLE && shape.labelFontSize ? shape.labelFontSize : areaFont;
          const labelFont = Math.round(Math.max(1, Math.min(requestedLabelFont, labelFontLimit)) * 10) / 10;
          const lineGap = Math.max(labelFont, areaFont) * 0.12;
          const textBlockHeight = labelFont + areaFont + lineGap;

          boxLabels.push(
            <Group
              key={`box-label-${shape.id}`}
              id={`box-label-${shape.id}`}
              x={shape.x}
              y={shape.y}
              scaleX={shape.scaleX}
              scaleY={shape.scaleY}
              rotation={shape.rotation}
              listening={false}
            >
              {shape.labelPosition || shape.labelScale !== undefined ? (
                <BoxShapeLabel
                  label={shape.label}
                  areaText={`${roundNumber((shape.width / gridCellSize) * (shape.height / gridCellSize))} m²`}
                  width={shape.width}
                  height={shape.height}
                  scaleX={shape.scaleX}
                  scaleY={shape.scaleY}
                  labelPosition={shape.labelPosition}
                  labelScale={shape.labelScale}
                  labelFontSize={shape.labelFontSize}
                />
              ) : (
                <Group
                  x={shape.width / 2}
                  y={shape.height / 2}
                  scaleX={1 / scaleX}
                  scaleY={1 / scaleY}
                  clipX={-visualWidth / 2}
                  clipY={-visualHeight / 2}
                  clipWidth={visualWidth}
                  clipHeight={visualHeight}
                >
                  <Text
                    text={labelLine}
                    width={visualWidth}
                    offsetX={visualWidth / 2}
                    y={-textBlockHeight / 2}
                    fontSize={labelFont}
                    align="center"
                    wrap="none"
                  />
                  <Text
                    text={areaLine}
                    width={visualWidth}
                    offsetX={visualWidth / 2}
                    y={-textBlockHeight / 2 + labelFont + lineGap}
                    fontSize={areaFont}
                    align="center"
                    wrap="none"
                  />
                </Group>
              )}
            </Group>,
          );

          return (
            <Group
              key={shape.id}
              name="shape"
              onContextMenu={deleteShape}
              id={shape.id}
              x={shape.x}
              y={shape.y}
              scaleX={shape.scaleX}
              scaleY={shape.scaleY}
              width={shape.width}
              height={shape.height}
              rotation={shape.rotation}
              {...commonOptions}
              onDragMove={(event) => {
                onDragMove(event);
                syncBoxLabel(event.target);
              }}
              onTransform={(event) => {
                syncBoxLabel(event.target);
              }}
            >
              <Rect
                id={shape.id}
                width={shape.width}
                height={shape.height}
                fill={shape.fill}
                stroke={shape.stroke}
                strokeWidth={shape.strokeWidth}
                cornerRadius={shape.cornerRadius}
                strokeScaleEnabled={false}
              />
            </Group>
          );
        }
        case ShapeType.CIRCLE:
          return (
            <Group
              key={shape.id}
              name="shape"
              onContextMenu={deleteShape}
              id={shape.id}
              x={shape.x}
              y={shape.y}
              scaleX={shape.scaleX}
              scaleY={shape.scaleY}
              width={shape.radiusX * 2}
              height={shape.radiusY * 2}
              rotation={shape.rotation}
              {...commonOptions}
            >
              <Ellipse
                key={shape.id}
                id={shape.id}
                fill={shape.fill}
                stroke={shape.stroke}
                radiusX={shape.radiusX}
                radiusY={shape.radiusY}
                strokeWidth={shape.strokeWidth}
                cornerRadius={shape.cornerRadius}
                strokeScaleEnabled={false}
                width={shape.radiusX * 2}
                height={shape.radiusY * 2}
              />
              <Text
                text={shape.label}
                x={shape.radiusX / fontSize / 2}
                y={shape.radiusY / fontSize / 2}
                fontSize={fontSize}
                offsetX={shape.label.length * (fontSize / 4)}
                offsetY={shape.radiusY / fontSize}
                align="center"
                verticalAlign="middle"
              />
            </Group>
          );
        case ShapeType.LINE:
          return <Line key={shape.id} name="shape" {...shape} {...commonOptions} x={0} y={0} onContextMenu={deleteShape} />;
        case ShapeType.TEXT:
          return (
            <Text
              key={shape.id}
              name="shape"
              {...shape}
              text={shape.label}
              strokeWidth={1}
              {...commonOptions}
              onContextMenu={deleteShape}
            />
          );
        case ShapeType.POLYGON:
          return (
            <Line
              key={shape.id}
              name="shape"
              {...shape}
              {...commonOptions}
              draggable={!shape.locked && activeTool === WarehouseMapTool.POINTER}
              x={0}
              y={0}
              closed
              onContextMenu={deleteShape}
            />
          );
        case ShapeType.POLYGON_BOX:
          return (
            <Group
              key={shape.id}
              name="shape"
              onContextMenu={deleteShape}
              id={shape.id}
              rotation={shape.rotation}
              {...commonOptions}
              draggable={!shape.locked && activeTool === WarehouseMapTool.POINTER}
            >
              <Line key={shape.id} name="shape" {...shape} {...commonOptions} x={0} y={0} closed />
              <Text
                text={polygonBoxText}
                x={findPolygonCenter(shape.points)[0]}
                y={findPolygonCenter(shape.points)[1]}
                offsetX={(polygonBoxText.length * (fontSize / 4)) / 2}
                offsetY={fontSize / 2}
                fontSize={fontSize}
                align="center"
                verticalAlign="middle"
              />
            </Group>
          );
        default:
          return null;
      }
    });
  };

  return (
    <>
      {getShapes()}
      {boxLabels}
    </>
  );
});
